/**
 * The front page's words, as data, kept apart from the page so the page,
 * its markdown copy for agents and its head say the same thing
 * (design/artboards/SiteJobs.dc.html, signed out). Every claim is one the
 * product makes true: postings are read from each company's own careers
 * site, a tailored resume is checked against the person's own, and the
 * extension fills the company's form and leaves Submit to the person.
 */

/** The page's heading, signed out. */
export const HOME_HEADLINE = "Autofill job applications. Free forever.";

/** Under the heading. */
export const HOME_SUMMARY =
    "Fill forms from your saved resume. Get an AI-tailored resume when you want one. You review and submit.";

/**
 * The hero's heading, signed out, a line each (design: the front page's
 * "Less typing. More applying." hero). The page's title and its markdown
 * copy keep HOME_HEADLINE, which says what the product is.
 */
export const HERO_LINES = ["Less typing.", "More applying."] as const;

/** Under the hero's heading. */
export const HERO_SUMMARY =
    "Your resume, autofilled on every application, or turbocharge and have AI tailor it for you.";

/** The hero's button, and the three facts under it. */
export const HERO_ACTION = "Get free autofill";
export const HERO_FACTS = ["Free forever", "Desktop extension", "No card needed"] as const;

/**
 * The hero's example: a stored resume filling the first fields of an
 * application. A made-up person, as the rest of lib/example.ts.
 */
export const HERO_EXAMPLE = {
    eyebrow: "Your resume. Already filled in.",
    file: "Alex Morgan · Resume.pdf",
    title: "Your application",
    tag: "Example preview",
    fields: [
        { label: "Full name", value: "Alex Morgan" },
        { label: "Email address", value: "alex@example.com" },
    ],
    done: "Details filled. You take it from here.",
} as const;

/** Over the search, under the hero. */
export const SEARCH_HEADLINE = "Find your next role";

/**
 * The longer account of the product, for the schema.org data and the
 * markdown copy, where there is room for it.
 */
export const HOME_LEDE =
    "Create a free account, save your resume and install the desktop browser extension. Reuse your details and saved answers without spending a tailor credit. Some fields still need you. Request an AI-tailored resume for a particular job when you want one, then review the form and press Submit.";

/** Free's allowance, as billing's catalogue gives it (`docs/pricing.md`). */
export const FREE_TAILORS = 10;

/** What a free account adds, said once. */
export const HOME_OFFER = `Autofill free forever. Optional AI tailoring includes ${FREE_TAILORS} free AI-tailored resumes every month.`;

/** The search's button. */
export const SEARCH_ACTION = "Search jobs";

/**
 * The install strip's headline: the AI first, as it is the main feature.
 * No allowance here, as a paid member sees the strip too.
 */
export const STRIP_HEADLINE = "AI tailors your resume to each job.";

/**
 * The strip's pill. It names autofill, as a bare "Free" beside the
 * headline would promise free tailoring. Autofill from resume fills the
 * person's details, saved answers and resume file, uses no tailor, and is
 * offered on every form (`apps/extension/src/content/run.ts`), so it has
 * no limit on any plan.
 */
export const STRIP_PILL = "Autofill free forever";

/** Under the strip's headline. */
export const STRIP_LINE =
    "Built from your real experience, then placed on the application by the desktop extension. Finish anything left, then submit yourself.";

/** Beside the store links, in the install dialog and the markdown copy. */
export const EXTENSION_LINE =
    "Use the desktop browser extension with a free account and saved resume. Fill supported fields, finish anything left, then submit yourself.";

export interface Step {
    /** The step's name, in the small caps over it. */
    label: string;
    /** The example, in the display type. */
    headline: string;
    line: string;
}

/** Four steps from account setup to submitting a reviewed application. */
export const FLOW_TITLE = "Less typing, from resume to form.";
export const FLOW: readonly Step[] = [
    {
        label: "Set up once",
        headline: "Save your resume",
        line: "Create a free account and install the extension in your desktop browser.",
    },
    {
        label: "Autofill free forever",
        headline: "Skip the repeated typing",
        line: "Fill supported fields from your saved resume and answers. No tailor credit spent.",
    },
    {
        label: "Optional AI tailoring",
        headline: "A resume for this job",
        line: "Request a tailored resume when you want one. Your monthly AI allowance is separate from free autofill.",
    },
    {
        label: "You stay in control",
        headline: "You press Submit",
        line: "Review the filled form, finish any fields left for you, then submit it yourself.",
    },
];

/** The closing invitation to start with free autofill. */
export const BAND_HEADLINE = "Spend less time filling the same details.";
export const BAND_LINE =
    "Start with a free account and saved resume. Autofill runs in the desktop browser extension. No card needed.";
export const BAND_ACTION = "Get free autofill";

/** Shared explanation of what paid plans add. */
export const PRICING_EXPLANATION =
    "Autofill is free forever on every plan. Use a free account, saved resume and desktop browser extension. Plans change your monthly AI-tailored resume allowance, and paid plans write on our most capable model, never your autofill limit. Job search, fit scores and application records are included.";
